import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  JOB_EDITABLE_STATUSES,
  JobStatus,
  JobView,
  type JobContent,
  type JobDto,
} from '@mpr/shared';

import { api, ApiError } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageIdOf } from '../lib/destinations.js';
import { networkLabel } from '../lib/channels.js';
import type { TranslationKey } from '../i18n/translations.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * The questions, in the order they are asked.
 *
 * Scheduled first, because it is the only one the operator can still act on
 * before anything happens — the rest are reports of what already did, or is
 * doing. The seven raw statuses used to be the filter, and `PARTIAL` is not a
 * word anybody scheduling a post would think to look under.
 *
 * "Going out now" sits between them for the same reason it exists: it is no
 * longer something to change and not yet something that happened.
 *
 * The grouping itself lives in `@mpr/shared`, so this list only names the tabs.
 */
const VIEWS = [
  { id: JobView.SCHEDULED, label: 'jobs.view.scheduled' },
  { id: JobView.PUBLISHING, label: 'jobs.view.publishing' },
  { id: JobView.PUBLISHED, label: 'jobs.view.published' },
  { id: JobView.FAILED, label: 'jobs.view.failed' },
  { id: JobView.CANCELLED, label: 'jobs.view.cancelled' },
] as const satisfies ReadonlyArray<{ id: JobView; label: TranslationKey }>;

/**
 * What a post says and shows, on its row.
 *
 * Two lines of text and a strip of thumbnails. Deliberately not the whole
 * post: this is a list somebody scans to find one row, and a post that renders
 * in full turns twenty rows into twenty screens.
 */
function JobPreview({ content }: { content: JobContent }) {
  const { t } = useI18n();

  const pictures = [
    ...content.media.map((file) => ({ key: file.id, src: `/api/media/${file.id}` })),
    ...content.imageUrls.map((url) => ({ key: url, src: url })),
  ];

  return (
    <div className="job-preview">
      {content.message ? (
        <p className="job-preview-text">{content.message}</p>
      ) : (
        <p className="tiny muted">{t('common.noText')}</p>
      )}

      {content.link && (
        <a className="tiny ltr" href={content.link} target="_blank" rel="noreferrer">
          {content.link}
        </a>
      )}

      {pictures.length > 0 && (
        <div className="job-preview-media">
          {pictures.map((picture) => (
            /* Decorative here: the text above already says what the post is,
               and a filename read aloud tells a screen reader nothing. */
            <img key={picture.key} src={picture.src} alt="" loading="lazy" />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Change a post that has not gone out yet — or one that tried and could not.
 *
 * Opens under the row rather than in the publish flow. The publish flow is
 * three steps for choosing content and pages, and neither of those is what
 * somebody fixing a typo on Thursday came here to do.
 *
 * Under "needs attention" it is the same form doing a different job. A post
 * usually fails because of what it says — a link Meta refused, a picture it
 * would not take — and Retry on its own only fails again, more slowly. Fix the
 * text here, then press Retry: the corrected version is what goes out.
 *
 * Two things it does not offer there. The hour, because a retry goes out when
 * it is pressed and a time field would promise otherwise. And, on a post that
 * reached some of its pages, any change to those — they already have the old
 * version, so the form says so rather than implying an edit reaches back.
 *
 * Which pages it goes to is not editable: a target holds a reservation on this
 * content for its page, and moving one means releasing and re-acquiring
 * reservations — half-right, that publishes twice. Cancelling and scheduling
 * again is the honest way, and the empty state says so.
 */
function EditJob({
  job,
  onDone,
  onError,
}: {
  job: JobDto;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useI18n();

  const draft = job.draft;

  const [message, setMessage] = useState(draft?.message ?? '');
  const [link, setLink] = useState(draft?.link ?? '');
  const [firstComment, setFirstComment] = useState(draft?.firstComment ?? '');
  /** The files already attached, minus any removed here, plus any added. */
  const [media, setMedia] = useState(draft?.media ?? []);
  const [uploading, setUploading] = useState(false);

  /** Still ahead of its hour, as opposed to being corrected after a failure. */
  const waiting = job.status === JobStatus.SCHEDULED;

  /*
   * A local time for a datetime-local input, which has no notion of zone. The
   * operator means nine where they are, and the browser is the only place that
   * knows where that is — so it is resolved here and sent as an instant.
   */
  const [at, setAt] = useState(() => {
    if (!job.scheduledAt) return '';
    const when = new Date(job.scheduledAt);
    const local = new Date(when.getTime() - when.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 16);
  });

  const save = useMutation({
    mutationFn: () =>
      api.editJob(job.id, {
        ...(draft
          ? {
              message,
              // Empty means "no link", which is a change and not an omission —
              // so it is sent as null rather than left out.
              link: link.trim() ? link.trim() : null,
              firstComment: firstComment.trim() ? firstComment.trim() : null,
              mediaAssetIds: media.map((file) => file.id),
            }
          : {}),
        // Sent only where it can be honoured. The server refuses it on a post
        // that has already run, and sending one it would refuse turns a saved
        // correction into an error message.
        ...(waiting && at ? { scheduledAt: new Date(at).toISOString() } : {}),
      }),
    onSuccess: onDone,
    onError: (error: unknown) =>
      onError(error instanceof ApiError ? error.message : t('common.requestFailed')),
  });

  /**
   * Upload straight away, attach on save.
   *
   * The file has to reach the server before it can be named in a list of ids,
   * and doing it on Save would make one button mean "send three files and then
   * change a post", which fails halfway in a way nobody can act on.
   */
  const attach = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);

    try {
      for (const file of Array.from(files)) {
        const { asset } = await api.uploadMedia(file);
        setMedia((current) => [
          ...current,
          { id: asset.id, filename: asset.filename, mimeType: file.type },
        ]);
      }
    } catch (error) {
      onError(error instanceof ApiError ? error.message : t('common.requestFailed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="assign-pages">
      {/* Only where there is text of ours to change. A job republishing
          somebody else's post has none, and the server refuses it. */}
      {draft ? (
        <>
          <label>
            {t('jobs.edit.message')}
            <textarea
              rows={4}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>

          <label>
            {t('jobs.edit.link')}
            <input
              type="url"
              className="ltr"
              value={link}
              placeholder="https://"
              onChange={(event) => setLink(event.target.value)}
            />
          </label>

          <label>
            {t('jobs.edit.firstComment')}
            <textarea
              rows={2}
              value={firstComment}
              onChange={(event) => setFirstComment(event.target.value)}
            />
          </label>

          <div className="job-edit-media">
            <span className="tiny muted">{t('jobs.edit.media')}</span>

            {media.length > 0 && (
              <div className="job-preview-media">
                {media.map((file) => (
                  <figure key={file.id}>
                    {/* Video shows as a name rather than a still: a poster
                        frame would need the file decoded, and the point here is
                        to recognise what is attached, not to watch it. */}
                    {file.mimeType.startsWith('video/') ? (
                      <span className="tiny">{file.filename}</span>
                    ) : (
                      <img src={`/api/media/${file.id}`} alt="" loading="lazy" />
                    )}
                    <button
                      type="button"
                      className="link tiny"
                      onClick={() => setMedia(media.filter((entry) => entry.id !== file.id))}
                    >
                      {t('common.remove')}
                    </button>
                  </figure>
                ))}
              </div>
            )}

            <input
              type="file"
              multiple
              accept="image/*,video/*"
              disabled={uploading}
              onChange={(event) => {
                void attach(event.target.files);
                // Cleared so the same file can be chosen again after a removal.
                event.target.value = '';
              }}
            />
            {uploading && <span className="tiny muted">{t('jobs.edit.uploading')}</span>}
          </div>

          {/* Pictures Meta fetches for itself, which only an outside agent
              sends. Shown and not editable: the address belongs to whoever
              published through the API, and a person retyping it here would be
              guessing at somebody else's server. */}
          {draft.imageUrls.length > 0 && (
            <div className="job-preview-media">
              {draft.imageUrls.map((url) => (
                <img key={url} src={url} alt="" loading="lazy" />
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="tiny muted">{t('jobs.edit.noText')}</p>
      )}

      {/* Only while there is still an hour to move. A failed post goes out the
          moment Retry is pressed, and a time field on that row would be a
          promise the button does not keep. */}
      {waiting && (
        <label>
          {t('jobs.edit.at')}
          <input type="datetime-local" value={at} onChange={(event) => setAt(event.target.value)} />
        </label>
      )}

      {/* Said before saving, not after. A post that reached three pages of five
          keeps the old version on those three — the retry only carries the
          correction to the ones that failed. */}
      {job.status === JobStatus.PARTIAL && (
        <p className="tiny muted">{t('jobs.edit.partialWarning')}</p>
      )}

      {!waiting && <p className="tiny muted">{t('jobs.edit.thenRetry')}</p>}

      <p className="tiny muted">{t('jobs.edit.targetsFixed')}</p>

      <div className="actions">
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending || uploading}>
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" className="ghost small" onClick={onDone}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

export function JobsPanel() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();
  const [view, setView] = useState<JobView>(JobView.SCHEDULED);
  const [showHidden, setShowHidden] = useState(false);
  /** Narrow to one Page. The server refuses a Page that was never theirs. */
  const [pageId, setPageId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  /**
   * Empty means "however this tab is usually read" — soonest first in the
   * schedule, newest first in the records. Kept as its own value rather than
   * seeded with the default, so switching tabs does not carry one tab's
   * direction into another where it means the opposite thing.
   */
  const [sort, setSort] = useState<'' | 'asc' | 'desc'>('');
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Which row is open for editing. One at a time, like every other row form here. */
  const [editing, setEditing] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);


  const jobs = useQuery({
    queryKey: ['jobs', view, showHidden, pageId, from, to, sort],
    queryFn: () =>
      api.jobs({
        view,
        includeHidden: showHidden,
        // A social account is picked from the same list; its value carries a prefix.
        // `ig:<page>` is the Instagram account linked to that Page: same row id, its own channel.
        pageId: pageIdOf(pageId) || (pageId.startsWith('ig:') ? pageId.slice(3) : undefined),
        channel: pageId.startsWith('ig:') ? 'INSTAGRAM' : undefined,
        socialAccountId: pageId.startsWith('soc:') ? pageId.slice(4) : undefined,
        from: from || undefined,
        to: to || undefined,
        sort: sort || undefined,
      }),
    /*
     * Jobs run in a background worker, so the panel polls rather than waiting
     * for a user action to reveal progress — but only where something is
     * actually moving. Published and cancelled are settled records, and polling
     * them every four seconds spends a query per reader on a number that
     * cannot change.
     */
    refetchInterval:
      view === JobView.SCHEDULED || view === JobView.PUBLISHING || view === JobView.FAILED
        ? 4_000
        : false,
  });

  const retry = useMutation({
    mutationFn: api.retryJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });

  const cancel = useMutation({
    mutationFn: api.cancelJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });

  const hide = useMutation({
    mutationFn: api.hideJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });

  const reveal = useMutation({
    mutationFn: api.revealJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });

  const rows = jobs.data?.jobs ?? [];

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('jobs.title')}</h2>
        {/* Offered only to somebody who can act on it. A publisher cannot
            reveal a hidden row, so the control would be a question they are
            not allowed to ask. */}
        {jobs.data?.maySeeHidden && view === JobView.CANCELLED && (
          <label className="check-row">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(event) => setShowHidden(event.target.checked)}
            />
            <span className="tiny">{t('jobs.showHidden')}</span>
          </label>
        )}
      </div>

      {/* The counts come from the server, off the same filters as the list — a
          tab reading 2 above a list showing 1 is worse than no number at all. */}
      <nav className="subtabs">
        {VIEWS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={view === entry.id ? 'subtab active' : 'subtab'}
            onClick={() => setView(entry.id)}
          >
            {t(entry.label)}
            {jobs.data ? ` (${jobs.data.counts[entry.id] ?? 0})` : ''}
          </button>
        ))}
      </nav>

      {/*
        The filters, under the tabs and above the list.

        They are here rather than compiled in because the question changes with
        the day: one Page this morning, last week's posts this afternoon,
        oldest first while working through a backlog. Every one of them is a
        control the operator turns, and none of them needs anybody to edit and
        deploy anything.
      */}
      <div className="inline-form job-filters">
        <DestinationFilter
          feature={DestinationFeature.JOBS}
          value={pageId}
          allLabel={t('inbox.allPages')}
          onChange={setPageId}
        />

        {/* Named for the date each tab is actually read by — the hour a post
            goes out in the schedule, the day it was made everywhere else — so
            the range cannot be read as filtering something it does not. */}
        <label className="tiny">
          {view === JobView.SCHEDULED ? t('jobs.filter.goesOutFrom') : t('jobs.filter.madeFrom')}
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="tiny">
          {t('jobs.filter.to')}
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>

        <select
          value={sort}
          onChange={(event) => setSort(event.target.value as '' | 'asc' | 'desc')}
        >
          <option value="">{t('jobs.filter.sortDefault')}</option>
          <option value="asc">{t('jobs.filter.sortAsc')}</option>
          <option value="desc">{t('jobs.filter.sortDesc')}</option>
        </select>

        {/* Shown only when something is actually narrowed, so the ordinary
            case is not a screen with a "clear" button on it. */}
        {(pageId || from || to || sort) && (
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              setPageId('');
              setFrom('');
              setTo('');
              setSort('');
            }}
          >
            {t('jobs.filter.clear')}
          </button>
        )}
      </div>

      {notice && <div className="notice error">{notice}</div>}

      {rows.length === 0 && (
        <div className="empty-state">
          <Icon.list />
          {/* Named for the tab. "No jobs" under the cancelled tab reads as a
              failure to load; "nothing was cancelled" reads as good news. */}
          <p>{t(`jobs.empty.${view}` as TranslationKey)}</p>
        </div>
      )}

      {rows.map((job) => (
        <article key={job.id} className="card">
          <header className="card-head">
            <div>
              <span className={`badge ${statusClass(job.status)}`}>{job.status}</span>{' '}
              {job.hiddenAt && <span className="badge warn">{t('jobs.hidden')}</span>}{' '}
              <span className="mono tiny muted">{job.id.slice(0, 8)}</span>
            </div>
            <div className="actions" style={{ marginTop: 0 }}>
              <button
                type="button"
                className="ghost small"
                onClick={() => setExpanded(expanded === job.id ? null : job.id)}
              >
                {expanded === job.id ? t('jobs.hideAttempts') : t('jobs.showAttempts')}
              </button>
              {/* Both only while it is still waiting on its clock. Once a
                  worker has it there is nothing to call back and nothing safe
                  to change, and the server refuses either. */}
              {/* Wherever the content can still be changed — which now includes
                  a post that failed, because retrying what Meta refused
                  unchanged refuses it again. */}
              {JOB_EDITABLE_STATUSES.includes(job.status) && (
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => setEditing(editing === job.id ? null : job.id)}
                >
                  {t('common.edit')}
                </button>
              )}
              {job.status === 'SCHEDULED' && (
                <button
                  type="button"
                  className="danger small"
                  onClick={() => cancel.mutate(job.id)}
                  disabled={cancel.isPending}
                >
                  {t('publish.cancelScheduled')}
                </button>
              )}
              {(job.status === 'FAILED' || job.status === 'PARTIAL') && (
                <button
                  type="button"
                  className="small"
                  onClick={() => retry.mutate(job.id)}
                  disabled={retry.isPending}
                >
                  <Icon.refresh />
                  {t('jobs.retryFailed')}
                </button>
              )}
              {/* Taking a settled row off the list. Never a delete: it is the
                  record of a decision, and the complaint it answers is a
                  cluttered list, not a wrong one. */}
              {job.status === 'CANCELLED' && !job.hiddenAt && (
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => hide.mutate(job.id)}
                  disabled={hide.isPending}
                >
                  {t('jobs.hide')}
                </button>
              )}
              {job.hiddenAt && (
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => reveal.mutate(job.id)}
                  disabled={reveal.isPending}
                >
                  {t('jobs.reveal')}
                </button>
              )}
            </div>
          </header>

          <p className="muted small">
            {job.sourceKind} → {job.publishKind} ·{' '}
            {t('jobs.created', { date: fmt.dateTime(job.createdAt) })}
            {/* The moment it is waiting for, named on the row. A scheduled job
                that only says "SCHEDULED" leaves the one question unanswered. */}
            {job.scheduledAt && (
              <>
                {' · '}
                <strong>{t('jobs.goesOut', { date: fmt.dateTime(job.scheduledAt) })}</strong>
              </>
            )}
          </p>

          {/* What this post actually says, on the row. The list used to show an
              id, a status and a page name, so "which of these was the shoe
              sale?" could not be answered from the screen that exists to
              answer it. */}
          {job.content && <JobPreview content={job.content} />}

          {editing === job.id && (
            <EditJob
              job={job}
              onDone={() => {
                setEditing(null);
                void queryClient.invalidateQueries({ queryKey: ['jobs'] });
              }}
              onError={setNotice}
            />
          )}

          <div className="grid-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('jobs.targetPage')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('jobs.attempts')}</th>
                  <th>{t('jobs.http')}</th>
                  <th>{t('jobs.metaCode')}</th>
                  <th>{t('jobs.message')}</th>
                  <th>{t('jobs.postId')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {job.targets.map((target) => (
                  <tr key={target.targetId}>
                    <td>
                      {target.targetPageName}{' '}
                      <span className="badge tiny">{networkLabel(t, target.channel)}</span>
                      {target.variant && (
                        <>
                          {' '}
                          <span className="badge tiny">{t(`jobs.variant.${target.variant}` as TranslationKey)}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${statusClass(target.status)}`}>{target.status}</span>
                    </td>
                    <td>{target.attempts}</td>
                    <td>{target.httpStatus ?? '—'}</td>
                    <td className="mono tiny">
                      {target.metaErrorCode ?? '—'}
                      {target.metaErrorSubcode ? `/${target.metaErrorSubcode}` : ''}
                    </td>
                    <td className="text-cell small">{target.metaErrorMessage ?? '—'}</td>
                    <td className="mono tiny">{target.resultingPostId ?? '—'}</td>
                    <td>
                      {target.status === 'NEEDS_RECONCILIATION' && (
                        <ResolveButton targetId={target.targetId} jobId={job.id} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {expanded === job.id && <AttemptHistory jobId={job.id} />}
        </article>
      ))}
    </section>
  );
}

function AttemptHistory({ jobId }: { jobId: string }) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const detail = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.job(jobId),
  });

  if (detail.isLoading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="grid-wrap" style={{ marginTop: 10 }}>
      <table className="grid inset">
        <thead>
          <tr>
            <th>{t('common.page')}</th>
            <th>{t('jobs.attemptNumber')}</th>
            <th>{t('jobs.result')}</th>
            <th>{t('jobs.endpoint')}</th>
            <th>{t('jobs.http')}</th>
            <th>{t('jobs.metaCode')}</th>
            <th>{t('jobs.message')}</th>
            <th>{t('jobs.retryable')}</th>
            <th>{t('jobs.when')}</th>
          </tr>
        </thead>
        <tbody>
          {detail.data?.attempts.map((attempt, index) => (
            <tr key={index}>
              <td>
                {attempt.targetPageName}{' '}
                <span className="badge tiny">{networkLabel(t, attempt.channel)}</span>
              </td>
              <td>{attempt.attempt}</td>
              <td>
                <span className={`badge ${attempt.succeeded ? 'ok' : 'err'}`}>
                  {attempt.succeeded ? t('common.yes') : t('common.no')}
                </span>
              </td>
              <td className="mono tiny">{attempt.endpoint}</td>
              <td>{attempt.httpStatus ?? '—'}</td>
              <td className="mono tiny">{attempt.metaErrorCode ?? '—'}</td>
              <td className="text-cell small">
                {attempt.metaErrorMessage ?? attempt.resultingPostId ?? '—'}
              </td>
              <td>{attempt.retryable ? t('common.yes') : t('common.no')}</td>
              <td className="small">{fmt.dateTime(attempt.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResolveButton({ targetId, jobId }: { targetId: string; jobId: string }) {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const resolve = useMutation({
    mutationFn: (foundPostId: string | null) => api.resolveTarget(targetId, foundPostId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });

  return (
    <button
      type="button"
      className="ghost small"
      onClick={() => {
        const found = window.prompt(t('jobs.resolvePrompt'), '');
        if (found === null) return;
        resolve.mutate(found.trim() || null);
      }}
    >
      {t('jobs.resolve')}
    </button>
  );
}

function statusClass(status: string): string {
  if (status === 'SUCCEEDED') return 'ok';
  if (status === 'SCHEDULED') return 'accent';
  if (status === 'FAILED' || status === 'NEEDS_RECONCILIATION') return 'err';
  if (status === 'PARTIAL' || status === 'SKIPPED_DUPLICATE') return 'warn';
  if (status === 'RUNNING') return 'accent';
  return '';
}
